import { useEffect, useRef, useState } from "react";
import { galleryImages } from "@/mocks/bbCoffeeData";

export default function Gallery() {
  const sectionRef = useRef<HTMLElement>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const els = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section id="experience" ref={sectionRef} className="py-20 md:py-28 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12 reveal-on-scroll">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100 rounded-full">
              Gallery
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-4">
              Event <span className="text-primary-600">Experience</span>
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">
              Our event experiences successfully attracted a large number of customers. From bustling night markets to community gatherings, BeanhoodBrew brings the coffee experience everywhere.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((img, i) => (
              <div
                key={i}
                className={`reveal-on-scroll group relative rounded-2xl overflow-hidden cursor-pointer ${
                  img.large ? "lg:col-span-2 lg:row-span-2" : ""
                }`}
                onClick={() => setLightbox(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setLightbox(i); }}
                aria-label={`View gallery image: ${img.caption}`}
              >
                <img
                  alt={img.alt}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  src={img.src}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-sm font-medium text-background-50">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 w-10 h-10 bg-background-50/10 hover:bg-background-50/20 rounded-full flex items-center justify-center text-background-50 transition-all cursor-pointer"
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(lightbox > 0 ? lightbox - 1 : galleryImages.length - 1); }}
            className="absolute left-4 md:left-8 w-10 h-10 bg-background-50/10 hover:bg-background-50/20 rounded-full flex items-center justify-center text-background-50 transition-all cursor-pointer"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-line" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(lightbox < galleryImages.length - 1 ? lightbox + 1 : 0); }}
            className="absolute right-4 md:right-8 w-10 h-10 bg-background-50/10 hover:bg-background-50/20 rounded-full flex items-center justify-center text-background-50 transition-all cursor-pointer"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-line" />
          </button>
          <div className="max-w-4xl max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img
              alt={galleryImages[lightbox].alt}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
              src={galleryImages[lightbox].src}
            />
            <p className="text-center text-background-200 mt-3 text-sm">{galleryImages[lightbox].caption}</p>
          </div>
        </div>
      )}
    </>
  );
}